import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth, API } from "../App";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { 
  CheckCircle, ArrowLeft, PartyPopper, Clock, Send, Sparkles, Star, MessageSquare
} from "lucide-react";

const ExamResult = () => {
  const { examId } = useParams();
  const navigate = useNavigate();
  const { token, user } = useAuth();
  
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showFeedback, setShowFeedback] = useState(false);
  const [feedbackSubmitted, setFeedbackSubmitted] = useState(false);
  const [submittingFeedback, setSubmittingFeedback] = useState(false);
  const [feedback, setFeedback] = useState({ rating: 0, ease_of_use: 0, question_quality: 0, comments: "" });

  useEffect(() => { fetchResult(); }, []);

  const fetchResult = async () => {
    try {
      const response = await axios.get(`${API}/candidate/exams/${examId}/result`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setResult(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to fetch result");
      navigate("/dashboard");
    } finally {
      setLoading(false);
    }
  };

  const submitFeedback = async () => {
    if (feedback.rating === 0 || feedback.ease_of_use === 0 || feedback.question_quality === 0) {
      toast.error("Please rate all categories");
      return;
    }
    setSubmittingFeedback(true);
    try {
      await axios.post(`${API}/candidate/exams/${examId}/feedback`, feedback, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Thank you for your feedback!");
      setFeedbackSubmitted(true);
    } catch (error) {
      if (error.response?.status === 400) {
        setFeedbackSubmitted(true);
      } else {
        toast.error("Failed to submit feedback");
      }
    } finally {
      setSubmittingFeedback(false);
    }
  };

  const StarRating = ({ value, onChange, label }) => (
    <div className="flex items-center justify-between">
      <Label className="text-sm text-slate-600">{label}</Label>
      <div className="flex gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <button key={star} onClick={() => onChange(star)} className="p-0.5 transition-transform hover:scale-110" data-testid={`${label.toLowerCase().replace(/ /g, '-')}-star-${star}`}>
            <Star className={`w-6 h-6 ${star <= value ? 'text-amber-400 fill-amber-400' : 'text-slate-200 hover:text-amber-200'}`} />
          </button>
        ))}
      </div>
    </div>
  );

  const formatTime = (seconds) => `${Math.floor(seconds / 60)}m ${seconds % 60}s`;

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-slate-50"><div className="spinner" /></div>;
  if (!result) return null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-blue-50" data-testid="exam-result">
      <header className="bg-white/80 backdrop-blur-sm border-b border-slate-200">
        <div className="max-w-5xl mx-auto px-6 py-4">
          <Button variant="ghost" onClick={() => navigate("/dashboard")} className="text-slate-600" data-testid="back-to-dashboard-btn">
            <ArrowLeft className="w-4 h-4 mr-2" />Back to Dashboard
          </Button>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-16">
        <Card className="border-0 shadow-2xl shadow-indigo-100/50 animate-fade-in overflow-hidden">
          <div className="bg-gradient-to-r from-indigo-600 to-blue-600 p-8 text-center">
            <div className="w-20 h-20 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center mx-auto mb-6 animate-pulse-soft">
              <CheckCircle className="w-10 h-10 text-white" />
            </div>
            <h1 className="text-3xl font-bold text-white mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>Exam Submitted Successfully!</h1>
            <p className="text-white/90 text-lg">Thank you for completing the assessment</p>
          </div>
          
          <CardContent className="p-8 text-center">
            <div className="flex items-center justify-center gap-2 mb-6">
              <Sparkles className="w-5 h-5 text-amber-500" />
              <span className="text-lg font-medium text-slate-900">{result.exam_name}</span>
              <Sparkles className="w-5 h-5 text-amber-500" />
            </div>
            
            <div className="bg-slate-50 rounded-2xl p-6 mb-8">
              <div className="flex items-center justify-center gap-3 mb-4">
                <PartyPopper className="w-6 h-6 text-indigo-600" />
                <h2 className="text-xl font-semibold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>Congratulations, {user?.full_name?.split(' ')[0]}!</h2>
              </div>
              <p className="text-slate-600 leading-relaxed">Your responses have been recorded and will be evaluated by our team.</p>
            </div>

            <div className="bg-indigo-50 border border-indigo-100 rounded-xl p-6 mb-8">
              <div className="flex items-center justify-center gap-2 mb-3">
                <Send className="w-5 h-5 text-indigo-600" />
                <h3 className="font-semibold text-indigo-900">What's Next?</h3>
              </div>
              <p className="text-indigo-700">Our HR team will review all submissions and contact shortlisted candidates via email.</p>
            </div>

            <div className="flex items-center justify-center gap-4 text-sm text-slate-500 mb-8">
              <span className="flex items-center gap-1"><Clock className="w-4 h-4" />Time: {formatTime(result.time_taken_seconds)}</span>
              <span className="w-1 h-1 rounded-full bg-slate-300" />
              <span>Questions: {result.total_questions}</span>
            </div>

            {/* Feedback Section */}
            {!feedbackSubmitted && !showFeedback && (
              <Button onClick={() => setShowFeedback(true)} variant="outline" className="border-indigo-200 text-indigo-600 hover:bg-indigo-50" data-testid="show-feedback-btn">
                <MessageSquare className="w-4 h-4 mr-2" />Share Your Feedback
              </Button>
            )}

            {showFeedback && !feedbackSubmitted && (
              <div className="mt-6 bg-white border border-slate-200 rounded-xl p-6 text-left animate-fade-in">
                <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                  <MessageSquare className="w-5 h-5 text-indigo-600" />Your Feedback
                </h3>
                <div className="space-y-4">
                  <StarRating label="Overall Experience" value={feedback.rating} onChange={(v) => setFeedback({ ...feedback, rating: v })} />
                  <StarRating label="Ease of Use" value={feedback.ease_of_use} onChange={(v) => setFeedback({ ...feedback, ease_of_use: v })} />
                  <StarRating label="Question Quality" value={feedback.question_quality} onChange={(v) => setFeedback({ ...feedback, question_quality: v })} />
                  <div>
                    <Label className="text-sm text-slate-600">Comments (Optional)</Label>
                    <Textarea value={feedback.comments} onChange={(e) => setFeedback({ ...feedback, comments: e.target.value })} placeholder="Share your thoughts about the exam..." rows={3} className="mt-1" data-testid="feedback-comments" />
                  </div>
                  <Button onClick={submitFeedback} disabled={submittingFeedback} className="w-full bg-indigo-600 hover:bg-indigo-700" data-testid="submit-feedback-btn">
                    {submittingFeedback ? "Submitting..." : "Submit Feedback"}
                  </Button>
                </div>
              </div>
            )}

            {feedbackSubmitted && (
              <div className="mt-6 bg-emerald-50 border border-emerald-200 rounded-xl p-4 animate-fade-in">
                <div className="flex items-center justify-center gap-2">
                  <CheckCircle className="w-5 h-5 text-emerald-600" />
                  <p className="text-emerald-700 font-medium">Thank you for your feedback!</p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        <div className="mt-8 flex justify-center">
          <Button onClick={() => navigate("/dashboard")} className="bg-indigo-600 hover:bg-indigo-700 px-8" size="lg" data-testid="return-dashboard-btn">Return to Dashboard</Button>
        </div>
        <p className="text-center text-slate-500 text-sm mt-6">Best of luck! We appreciate your interest in joining edSolutions360.</p>
      </main>
    </div>
  );
};

export default ExamResult;
